import { Color, Spacing } from '@persona-id/design-tokens';
import { Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { merge } from 'lodash';
import { useMemo, useState, VFC } from 'react';
import { Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import DraggableDivider from 'dashboard/components/WorkflowControlPanel/DraggableDivider';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { parseHandlebarsText } from 'dashboard/lib/utils/handlebars';
import { ExternalIntegrationProductivityOpenAi } from 'dashboard/models/external-integration';
import { WorkflowActionGPTGenerateMessageV1 } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import { JsonSchema } from 'lib/json-schema';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { Json } from 'lib/types';

const OPENAI_CHAT_COMPLETION_ENDPOINT = 'https://api.openai.com/v1/chat/completions';

const Well = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]};
  margin-top: ${Spacing[1]};
`;

const KeyValuesContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  flex-wrap: no-wrap;
`;

const StyledSticker = styled(Sticker)`
  position: relative;
  top: -${Spacing[3]};
  left: ${Spacing[4]};
  border: 1px solid ${Color.CorePurple200};
`;

const KeyValuePairContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: no-wrap;
  margin: ${Spacing[1]};
`;

const KeyInput = styled(Input)`
  margin: ${Spacing[1]};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${Spacing[3]};
`;

export const postToChatCompletion = async (
  payload: Json,
  apiKey: string,
  dispatch: Dispatch,
  setResponseString: (value: string) => void,
) => {
  try {
    const { data } = await axios({
      method: 'post',
      url: OPENAI_CHAT_COMPLETION_ENDPOINT,
      data: payload,
      headers: {
        Authorization: `Bearer ${apiKey}`,
        accept: 'application/json',
      },
    });
    dispatch(showMessage('Request sent successfully', MessageType.Success));
    setResponseString(data?.choices[0]?.message?.content);
  } catch (error) {
    dispatch(showMessage('Request failed to send, please try again later.', MessageType.Failure));
  }
};

const payloadBuilder = (params: WorkflowActionGPTGenerateMessageV1['params']) => {
  return {
    model: params['gpt-model'],
    messages: [{ role: 'user', content: params.prompt }],
    temperature: params.temperature,
    top_p: params['top-p'],
    presence_penalty: params['presence-penalty'],
    frequency_penalty: params['frequency-penalty'],
    max_tokens: params['max-tokens'],
  };
};

interface AIPromptLivePreviewPanelProps {
  title: string;
  externalIntegration: ExternalIntegrationProductivityOpenAi;
  params: WorkflowActionGPTGenerateMessageV1['params'];
  editable: boolean;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  onOutsideClick: () => void;
  onUpdatePrompt: (value: string) => void;
  idPortalPrefix: string;
}

const AIPromptLivePreviewPanel: VFC<AIPromptLivePreviewPanelProps> = ({
  title,
  externalIntegration,
  params,
  editable,
  jsonSchema,
  tokensToDisplayValue,
  onOutsideClick,
  onUpdatePrompt,
  idPortalPrefix,
}) => {
  const dispatch = useDispatch();

  const [variableToPayloadMap, setVariableToPayloadMap] = useState<Record<string, string>>({});

  const [responseString, setResponseString] = useState(
    'The response to your prompt will appear here.',
  );

  const { control } = useFormSecurely<{
    prompt: string;
  }>({
    defaultValues: {
      prompt: params['prompt'],
    },
  });

  const parsedText = useMemo(() => {
    const parsedText = parseHandlebarsText(params['prompt']);
    const currentVariables = parsedText
      .filter((text) => text.variableName)
      .map((text) => text.variableName);

    currentVariables.forEach((variable) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (!(variable in variableToPayloadMap)) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setVariableToPayloadMap(merge(variableToPayloadMap, { [variable]: '' }));
      }
    });
    const currentArr = Object.keys(variableToPayloadMap).filter((variable) =>
      currentVariables.includes(variable),
    );
    setVariableToPayloadMap(
      Object.fromEntries(currentArr.map((variable) => [variable, variableToPayloadMap[variable]])),
    );

    return parsedText;
  }, [params['prompt']]);

  const { callback: getPreviewResponse, loading: isLoading } = useApiCallback(
    async (mergedParams: Json) => {
      await postToChatCompletion(
        mergedParams,
        externalIntegration.attributes['api-key'],
        dispatch,
        setResponseString,
      );
    },
    [],
  );

  const handleSendPrompt = () => {
    const mappedPayload = {
      prompt: parsedText.reduce(
        (payload, parsedVariable) =>
          payload +
          (parsedVariable.variableName
            ? variableToPayloadMap[parsedVariable.variableName]
            : parsedVariable.content),
        '',
      ),
    };
    const mergedParams = payloadBuilder({ ...params, ...mappedPayload });
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    getPreviewResponse(mergedParams);
  };

  return (
    <MaximizablePanel
      title={title}
      outsideExceptionalIdPrefixes={[idPortalPrefix]}
      onOutsideClickAndEscPress={onOutsideClick}
      maximized={true}
    >
      {(fullScreen: boolean) => (
        <div style={{ height: fullScreen ? 'calc(100% - 48px)' : '450px' }}>
          <DraggableDivider
            orientation='horizontal'
            firstComponent={
              <div>
                <Controller
                  control={control}
                  name='prompt'
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <HandlebarFormControl
                      size='md'
                      type='text-area'
                      value={value}
                      updateValue={onChange}
                      onBlur={() => {
                        onUpdatePrompt(value);
                      }}
                      onMouseLeave={() => {
                        onUpdatePrompt(value);
                      }}
                      editable={editable}
                      placeholder='Write a paragraph asking about...'
                      allowedTypes={JsonSchemaStringifiableTypes}
                      jsonSchema={jsonSchema}
                      tokensToDisplayValue={tokensToDisplayValue}
                      id={`${idPortalPrefix}__prompt-input`}
                    />
                  )}
                />
                <Well>
                  <KeyValuesContainer>
                    {Object.entries(variableToPayloadMap).map(([key], idx) => (
                      <KeyValuePairContainer key={key}>
                        <KeyInput
                          size='md'
                          type='string'
                          key={`header-key-${idx}`}
                          value={key}
                          disabled={true}
                          id={`${idPortalPrefix}__key-${idx}`}
                        />
                        <KeyInput
                          size='md'
                          type='string'
                          key={`header-value-${idx}`}
                          onBlur={(e: { target: { value: string } }) => {
                            setVariableToPayloadMap(
                              merge(variableToPayloadMap, { [key]: e.target.value }),
                            );
                          }}
                          disabled={!editable}
                          placeholder='Sample Payload'
                          id={`${idPortalPrefix}__value-input-${idx}`}
                        />
                      </KeyValuePairContainer>
                    ))}
                  </KeyValuesContainer>
                  <ButtonContainer>
                    <Button
                      onClick={handleSendPrompt}
                      id={`${idPortalPrefix}__send-button`}
                      type='button'
                      size='md'
                      variant='basic'
                      width='auto'
                    >
                      Send Prompt
                    </Button>
                  </ButtonContainer>
                </Well>
              </div>
            }
            secondComponent={
              <>
                <StyledSticker id={`${idPortalPrefix}-response-sticker`} variant='primary'>
                  Response Preview:
                </StyledSticker>
                {isLoading ? (
                  <>
                    <Paragraph>
                      Depending on the selected model and OpenAI’s server performance, some
                      responses may take longer to load
                    </Paragraph>
                    <LoadingIndicator />
                  </>
                ) : (
                  responseString
                    ?.split('\n')
                    .map((text, idx) => <Paragraph key={idx}>{text}</Paragraph>)
                )}
              </>
            }
            firstMinHeight='20px'
            secondMinHeight='20px'
            firstStyle={{ overflow: 'hidden' }}
          />
        </div>
      )}
    </MaximizablePanel>
  );
};

export default AIPromptLivePreviewPanel;
